import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, KeyRound, UserPlus, Trash2, ShieldCheck } from "lucide-react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const Field = ({ label, children }) => (
  <div>
    <Label className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</Label>
    <div className="mt-2">{children}</div>
  </div>
);

export default function Settings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "operator" });
  const isAdmin = user?.role === "admin";
  const [creating, setCreating] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const loadUsers = () => api.get("/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));

  useEffect(() => {
    loadUsers();
  }, []);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) {
      toast.error("Les deux nouveaux mots de passe ne correspondent pas");
      return;
    }
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      setPwd({ current_password: "", new_password: "", confirm: "" });
      toast.success("Mot de passe modifié");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingPwd(false);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/users", newUser);
      setNewUser({ name: "", email: "", password: "", role: "operator" });
      toast.success("Compte créé");
      loadUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const removeUser = async () => {
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success("Compte supprimé");
      setToDelete(null);
      loadUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] px-4 py-8 sm:px-8" data-testid="settings-page">
      <div className="mx-auto max-w-4xl">
        <Button variant="ghost" className="mb-4 gap-2 pl-0 text-sm text-slate-600"
                onClick={() => navigate("/admin")} data-testid="settings-back-button">
          <ArrowLeft className="h-4 w-4" /> Retour au tableau de bord
        </Button>
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Back-office</p>
        <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
          Paramètres du compte
        </h1>
        <p className="mt-1 text-sm text-slate-600">Connecté en tant que {user?.email}</p>

        <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <KeyRound className="h-5 w-5 text-[#2563EB]" /> Changer mon mot de passe
          </h2>
          <form onSubmit={changePassword} className="mt-4 grid gap-4 sm:grid-cols-3"
                data-testid="change-password-form">
            <Field label="Mot de passe actuel">
              <Input required type="password" value={pwd.current_password} className="h-11"
                     onChange={(e) => setPwd((p) => ({ ...p, current_password: e.target.value }))}
                     data-testid="current-password-input" />
            </Field>
            <Field label="Nouveau mot de passe">
              <Input required type="password" minLength={8} value={pwd.new_password} className="h-11"
                     onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))}
                     data-testid="new-password-input" />
            </Field>
            <Field label="Confirmer">
              <Input required type="password" minLength={8} value={pwd.confirm} className="h-11"
                     onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                     data-testid="confirm-password-input" />
            </Field>
            <div className="sm:col-span-3">
              <Button type="submit" disabled={savingPwd}
                      className="h-11 bg-[#1E3A8A] font-bold text-white hover:bg-[#172d6d] sm:px-8"
                      data-testid="change-password-submit">
                {savingPwd ? "Enregistrement…" : "Modifier le mot de passe"}
              </Button>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-slate-400">
                8 caractères minimum
              </p>
            </div>
          </form>
        </section>

        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <ShieldCheck className="h-5 w-5 text-[#2563EB]" /> Comptes administrateurs
          </h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[560px] text-left text-sm" data-testid="users-table">
              <thead className="bg-slate-50 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Nom</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Rôle</th>
                  <th className="px-4 py-3">Créé le</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-3 font-semibold text-slate-900">{u.name}</td>
                    <td className="px-4 py-3 text-slate-700">
                      {u.email}
                      {u.email === user?.email && (
                        <span className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 font-mono text-[10px] uppercase text-[#2563EB]">
                          vous
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase ${
                          u.role === "admin"
                            ? "border-blue-300 bg-blue-50 text-[#1E3A8A]"
                            : "border-slate-300 bg-slate-50 text-slate-600"
                        }`}
                        data-testid={`user-role-${u.email}`}
                      >
                        {u.role === "admin" ? "Administrateur" : "Opérateur"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(u.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      {u.email !== user?.email && u.source !== "env" && isAdmin && (
                        <Button variant="outline" size="sm"
                                className="border-rose-300 text-[#DC2626] hover:bg-rose-50"
                                onClick={() => setToDelete(u)} data-testid={`delete-user-${u.email}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <form onSubmit={createUser} className="mt-6 grid gap-4 border-t border-slate-200 pt-6 sm:grid-cols-3"
                data-testid="new-user-form">
            {!isAdmin && (
              <p className="sm:col-span-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
                 data-testid="operator-notice">
                Votre compte est un compte opérateur : la gestion des comptes et les suppressions sont
                réservées à l'administrateur principal.
              </p>
            )}
            <Field label="Nom">
              <Input required value={newUser.name} className="h-11" placeholder="Correspondant Abidjan"
                     onChange={(e) => setNewUser((u) => ({ ...u, name: e.target.value }))}
                     data-testid="new-user-name-input" />
            </Field>
            <Field label="Email">
              <Input required type="email" value={newUser.email} className="h-11"
                     placeholder="collegue@haba-trans.ca"
                     onChange={(e) => setNewUser((u) => ({ ...u, email: e.target.value }))}
                     data-testid="new-user-email-input" />
            </Field>
            <Field label="Mot de passe">
              <Input required type="password" minLength={8} value={newUser.password} className="h-11"
                     onChange={(e) => setNewUser((u) => ({ ...u, password: e.target.value }))}
                     data-testid="new-user-password-input" />
            </Field>
            <Field label="Rôle">
              <Select value={newUser.role}
                      onValueChange={(v) => setNewUser((u) => ({ ...u, role: v }))}>
                <SelectTrigger className="h-11" data-testid="new-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">Opérateur (ne peut pas supprimer)</SelectItem>
                  <SelectItem value="admin">Administrateur (tous les droits)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="sm:col-span-3">
              <Button type="submit" disabled={creating || !isAdmin}
                      className="h-11 gap-2 bg-[#2563EB] font-bold text-white hover:bg-[#1D4ED8] sm:px-8"
                      data-testid="create-user-submit">
                <UserPlus className="h-4 w-4" />
                {creating ? "Création…" : "Créer le compte"}
              </Button>
            </div>
          </form>
        </section>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce compte ?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.email} ne pourra plus accéder à l'espace administrateur.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-user">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={removeUser} className="bg-[#DC2626] text-white hover:bg-[#b91c1c]"
                               data-testid="confirm-delete-user">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
